import React, { FC, useState } from 'react'
import { ArrayField, Controller } from 'react-hook-form'
import get from 'lodash/get'
import moment from 'moment'
import { Button } from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import { Box, makeStyles, createStyles } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { TIME_TYPE } from 'apps/fleet-planner/constants/loads'
import { useCreateLoadFormContext } from 'apps/fleet-planner/context'
import { IShipmentStop, ShipmentStopSchemaType } from 'apps/fleet-planner/schema'
import { FORMATS } from 'constants/datetime'
import { DateAndTimePicker } from './DateAndTimePicker'

const EmptyComponent = () => <></>

const isValidDate = (d: string | undefined): d is string => !!d && moment(d).isValid()

/**
 * Uses the previous stops endAt + 8 hours as default if available
 */
const getDefaultEndAtForStartAt = (
  value: string | undefined,
  timezone: string,
  offset: number = 8
): IShipmentStop['startAt'] => {
  if (!isValidDate(value)) return undefined
  return moment(value).tz(timezone).add(offset, 'hours').format(FORMATS.fleetPlannerApiFormat)
}

/**
 * Compares `startAt` and `endAt` values, and returns the diff
 */
const getHourDifference = (
  startAt: string | undefined,
  endAt: string | undefined,
  defaultOffset: number = 8
): number => {
  if (!isValidDate(startAt) || !isValidDate(endAt)) return defaultOffset
  const startAtMoment = moment(startAt)
  const endAtMoment = moment(endAt)
  return startAtMoment.diff(endAtMoment, 'hours')
}

type TClassKey =
  | 'appointmentContainer'
  | 'dateAndTimePickerContainer'
  | 'appointmentSpacerContainer'

const useControlledStopTimesStyles = makeStyles(theme =>
  createStyles<TClassKey, {}>({
    appointmentContainer: {
      display: 'flex',
      width: 'auto',
      flexWrap: 'wrap',
      flexGrow: 1,
      flexShrink: 1,
      marginBottom: theme.spacing(1),

      [theme.breakpoints.up('md')]: {
        marginBottom: 0,
        flexWrap: 'nowrap',
      },
    },
    appointmentSpacerContainer: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexShrink: 1,
      display: 'flex',
      alignItems: 'center',

      [theme.breakpoints.up('md')]: {
        paddingBottom: theme.spacing(1),
        marginBottom: 0,
      },
    },
    dateAndTimePickerContainer: {
      flex: '1 1 100%',
      width: '100%',
      marginBottom: theme.spacing(1),

      [theme.breakpoints.up('md')]: {
        marginBottom: 0,
        maxWidth: 242,
      },
    },
  })
)

interface IStopTimes {
  startAt?: string
  endAt?: string
}

interface IControlledStopTimesProps {
  stopField: Partial<ArrayField<IShipmentStop, 'id'>>
  fieldIndex: number
  displayTimezone: string
  defaultValues: IStopTimes
}

/**
 * Isolates state changes, but ties all RHF updates to changeHandler functions, without any useEffect hooks
 */
export const ControlledStopTimes: FC<IControlledStopTimesProps> = ({
  defaultValues,
  displayTimezone: displayTimezoneProp,
  fieldIndex,
  stopField,
}) => {
  const { errors, getValues, setValue } = useCreateLoadFormContext()
  const { isMobile } = useBreakpointContext()
  const classes = useControlledStopTimesStyles()

  const { endAt: endAtProp, startAt: startAtProp } = defaultValues || {}
  const [stopTimes, setStopTimes] = useState<IStopTimes>({
    startAt: startAtProp,
    endAt: endAtProp,
  })

  const { endAt, startAt } = stopTimes || {}

  const getInputName = (keyName: keyof ShipmentStopSchemaType): string => {
    return `stops[${fieldIndex}].${keyName}`
  }

  const [timeType, setTimeType] = useState<TIME_TYPE>(
    stopField.endAt ? TIME_TYPE.window : TIME_TYPE.appointment
  )

  const setEndAt = (newEndAt: IStopTimes['endAt']) => {
    newEndAt = isValidDate(newEndAt) ? newEndAt : undefined
    setValue(getInputName('endAt'), newEndAt, {
      shouldValidate: true,
    })
    setStopTimes(prev => ({
      ...prev,
      endAt: newEndAt,
    }))
  }

  const setStartAt = (newStartAt: IStopTimes['startAt']) => {
    newStartAt = isValidDate(newStartAt) ? newStartAt : undefined

    setValue(getInputName('startAt'), newStartAt, {
      shouldValidate: true,
    })

    setStopTimes(prev => ({
      ...prev,
      startAt: newStartAt,
    }))

    // If it's a window, also set the `endAt` value if there is none
    if (!isValidDate(endAt) && timeType === TIME_TYPE.window) {
      setEndAt(getDefaultEndAtForStartAt(newStartAt, displayTimezoneProp))

      // If there is an `endAt` value, maintain the difference in hours
    } else if (isValidDate(endAt) && timeType === TIME_TYPE.window) {
      const offset = getHourDifference(endAt, startAt)
      const defaultEndAt = getDefaultEndAtForStartAt(newStartAt, displayTimezoneProp, offset)
      setEndAt(defaultEndAt)
    }
  }

  /**
   * When toggling, set default `endAt` value in both outcomes
   */
  const timeTypeChangeHandler = (newValue: string) => {
    const { stops = [] } = getValues()
    const stop = stops[fieldIndex] || {}
    if (newValue === TIME_TYPE.window) {
      setTimeType(newValue as TIME_TYPE)
      if (!isValidDate(endAt)) {
        setEndAt(getDefaultEndAtForStartAt(stop.startAt, displayTimezoneProp))
      }
    } else if (newValue === TIME_TYPE.appointment) {
      setTimeType(newValue as TIME_TYPE)
      setEndAt(undefined)
    }
  }

  return (
    <Box display='flex' width='100%' flexWrap='wrap' mb={-1}>
      <Box flex={0} mr={1} mb={1}>
        <ButtonGroup
          hideLabel
          inline
          onChange={timeTypeChangeHandler}
          value={timeType}
          dataTest='assign-driver'>
          <Button
            value={TIME_TYPE.appointment}
            size={isMobile ? 'large' : undefined}
            onClick={() => {}}>
            Appt
          </Button>
          <Button value={TIME_TYPE.window} size={isMobile ? 'large' : undefined} onClick={() => {}}>
            FCFS
          </Button>
        </ButtonGroup>
      </Box>
      <Box className={classes.appointmentContainer}>
        <Box className={classes.dateAndTimePickerContainer}>
          {/* Start At */}
          <DateAndTimePicker
            dataTest={`${stopField.stopType}-${fieldIndex}-start-at`}
            timezone={displayTimezoneProp}
            value={stopTimes.startAt}
            onChange={setStartAt}
            large={isMobile}
            wrapValidationText
            validationMessage={get(errors, `stops[${fieldIndex}].startAt.message`, '')}
            useOffset={false}
          />
        </Box>
        {timeType === TIME_TYPE.window && (
          <>
            <Box className={classes.appointmentSpacerContainer}>to</Box>
            <Box className={classes.dateAndTimePickerContainer}>
              {/* End At */}
              <DateAndTimePicker
                dataTest={`${stopField.stopType}-${fieldIndex}-end-at`}
                timezone={displayTimezoneProp}
                value={stopTimes.endAt}
                onChange={setEndAt}
                large={isMobile}
                wrapValidationText
                validationMessage={get(errors, `stops[${fieldIndex}].endAt.message`, '')}
                useOffset={false}
              />
            </Box>
          </>
        )}
      </Box>
      {/**
       * For initializing values in RHF model. This is necessary before we can use
       * setValue() on either of these properties
       */}
      <Controller
        name={getInputName('startAt')}
        defaultValue={defaultValues.startAt}
        render={EmptyComponent}
      />
      <Controller
        name={getInputName('endAt')}
        defaultValue={defaultValues.endAt}
        render={EmptyComponent}
      />
    </Box>
  )
}
